"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isInMonthDayRange = exports.validateMonthAndDay = exports.isBetweenStartAndEndDates = exports.isBetween = exports.isSameDateOrEarlier = exports.isSameDateOrLater = exports.sameDay = exports.isSameTime = exports.isSameDateTime = exports.isToday = exports.isInFuture = exports.isSameDate = exports.isLater = exports.isEarlier = exports.isValidDate = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const customParseFormat_1 = __importDefault(require("dayjs/plugin/customParseFormat"));
const utils_1 = require("../utils");
const types_1 = require("../types");
dayjs_1.default.extend(customParseFormat_1.default);
const MONTHS_IN_YEAR = 12;
const FIRST_MONTH_OF_YEAR = 1;
const MAX_DAYS_IN_MONTH = 31;
const FIRST_DAY_OF_MONTH = 1;
const INVALID = { valid: false };
/**
 * Check if a date is valid.
 * @param date The date to check.
 * @param format The format of the date.
 * @returns A boolean indicating whether the date is valid.
 */
function isValidDate(date, format) {
    return (0, dayjs_1.default)(date, format).isValid();
}
exports.isValidDate = isValidDate;
/**
 * Check if a date is earlier than another date.
 * @param date The date to check.
 * @param other the date to compare.
 * @param format The format of the date.
 * @returns A boolean indicating whether the date is earlier than the other date.
 */
function isEarlier(date, other, format) {
    return (0, dayjs_1.default)(date).isBefore(other, format);
}
exports.isEarlier = isEarlier;
/**
 * Check if a date is later than another date.
 * @param date The date to check.
 * @param other the date to compare.
 * @param format The format of the date.
 * @returns A boolean indicating whether the date is later than the other date.
 */
function isLater(date, other, format) {
    return (0, dayjs_1.default)(date).isAfter(other, format);
}
exports.isLater = isLater;
/**
 * Checks if two dates are the same.
 * @param date The first date to compare.
 * @param other The second date to compare.
 * @param unit The unit of time to compare (default: 'date').
 * @returns A boolean indicating whether the two dates are the same.
 */
function isSameDate(date, other, unit = 'date') {
    return (0, dayjs_1.default)(date).isSame(other, unit);
}
exports.isSameDate = isSameDate;
/**
 * Check if a date is in the future.
 * @param date The date to check.
 * @returns A boolean indicating whether the date is in the future.
 */
function isInFuture(date) {
    return (0, dayjs_1.default)(date).isAfter((0, dayjs_1.default)());
}
exports.isInFuture = isInFuture;
/**
 * Checks if the given date is in the future.
 *
 * @param date - The date to check.
 * @returns A boolean indicating whether the date is in the future.
 */
function isToday(date) {
    return isSameDate(date, new Date());
}
exports.isToday = isToday;
/**
 * Checks if two date-time values are the same.
 *
 * @param date1 - The first date value.
 * @param time1 - The first time value.
 * @param date2 - The second date value.
 * @param time2 - The second time value.
 * @returns A boolean indicating whether the date-time values are the same.
 */
function isSameDateTime(date1, time1, date2, time2) {
    return sameDay(date1, date2) && time1 === time2;
}
exports.isSameDateTime = isSameDateTime;
/**
 * Checks if two times are the same.
 *
 * @param time1 - The first time to compare.
 * @param time2 - The second time to compare.
 * @returns True if the times are the same, false otherwise.
 */
function isSameTime(time1, time2) {
    return time1 === time2;
}
exports.isSameTime = isSameTime;
/**
 * Checks if two dates are the same day.
 * @param d1 - The first date.
 * @param d2 - The second date.
 * @returns True if the dates are the same day, false otherwise.
 * @throws {CustomError} If either of the dates is invalid.
 */
function sameDay(d1, d2) {
    const startOfDay1 = (0, utils_1.getStartOfDay)(d1, true);
    const startOfDay2 = (0, utils_1.getStartOfDay)(d2, true);
    if (!(startOfDay1 instanceof Date) || !(startOfDay2 instanceof Date)) {
        throw new types_1.CustomError(types_1.errorCode.invalidDate, 'Invalid Date');
    }
    return startOfDay1.getTime() === startOfDay2.getTime();
}
exports.sameDay = sameDay;
/**
 * Checks if a given date is the same or later than another date.
 * NB: Ignores time portion of params.
 *
 * @param date - The first date to compare, ignoring time portion of param.
 * @param other - The second date to compare, ignoring time portion of param.
 * @param format - The format of the date.
 * @returns `true` if the first date is the same or later than the second date, `false` otherwise.
 */
function isSameDateOrLater(date, other, format) {
    return isSameDate(date, other, format) || isLater(date, other, format);
}
exports.isSameDateOrLater = isSameDateOrLater;
/**
 * Checks if a given date is the same or earlier than another date.
 * NB: Ignores time portion of params.
 *
 * @param date - The first date to compare, ignoring time portion of param.
 * @param other - The second date to compare, ignoring time portion of param.
 * @param format - The format of the date.
 * @returns `true` if the first date is the same or earlier than the second date, `false` otherwise.
 */
function isSameDateOrEarlier(date, other, format) {
    return isSameDate(date, other, format) || isEarlier(date, other, format);
}
exports.isSameDateOrEarlier = isSameDateOrEarlier;
/**
 * Checks if a given date is between two other dates.
 * @param date {DateInput} The date to check.
 * @param start {DateInput} The start date.
 * @param end {DateInput} The end date.
 * @returns {boolean} A boolean indicating whether the date is between the start and end dates.
 */
function isBetween(date, start, end) {
    return !isEarlier(date, start) && !isLater(date, end);
}
exports.isBetween = isBetween;
/**
 * Check if a date is between a start and end date.
 * @param date {DateInput} - The date to check.
 * @param startDate {DateInput} - The start date of the range.
 * @param endDate {DateInput} - The end date of the range.
 * @returns {boolean} - True if the date is within the range, false otherwise.
 */
function isBetweenStartAndEndDates(date, startDate, endDate) {
    return isValidDate(date) && !isEarlier(date, startDate) && !isLater(date, endDate);
}
exports.isBetweenStartAndEndDates = isBetweenStartAndEndDates;
function validateMonthAndDay(dateNoYear) {
    const [month, day, empty] = dateNoYear.replace(/\//g, '-').split('-');
    if (empty) {
        return INVALID;
    }
    if (isNaN(+month) || isNaN(+day)) {
        return INVALID;
    }
    if (+month < FIRST_MONTH_OF_YEAR || +month > MONTHS_IN_YEAR) {
        return INVALID;
    }
    if (+day < FIRST_DAY_OF_MONTH || +day > MAX_DAYS_IN_MONTH) {
        return INVALID;
    }
    return { month: +month, day: +day, valid: true };
}
exports.validateMonthAndDay = validateMonthAndDay;
function isInMonthDayRange(activityTime, date) {
    let startDate, endDate;
    const start = validateMonthAndDay(activityTime.availabilityStartDate);
    if (!start.valid) {
        throw new Error(`invalid start day/month ${activityTime.availabilityStartDate} in ${JSON.stringify(activityTime)}`);
    }
    const end = validateMonthAndDay(activityTime.availabilityEndDate);
    if (!end.valid) {
        throw new Error(`invalid end day/month ${activityTime.availabilityEndDate} in ${JSON.stringify(activityTime)}`);
    }
    const checkedDate = (0, utils_1.convertStringToDate)(date);
    const checkedYear = checkedDate.getFullYear();
    const checkedMonth = checkedDate.getMonth() + 1; // cause in JS months are zero based ==> January = 0
    const checkedDay = checkedDate.getDate();
    // same date
    if (checkedMonth === start.month && checkedDay == start.day) {
        return true;
    }
    if (checkedMonth === end.month && checkedDay == end.day) {
        return true;
    }
    if (start.month > end.month || (start.month === end.month && start.day > end.day)) {
        // this is an over december range - e.g. 11/01 => 03/01 (Nov => March)
        // we have 2 options:
        // 1. the date's comes before the end date (e.g. in the prev example - it's Jan/Feb/March)
        // 2. the date's comes after ( e.g. March => Dec)
        // case 1 - we need to stay with the same year ()
        // case 2 - we need to add 1 to the year
        let startYear = checkedYear;
        (end.month > checkedMonth || (end.month === checkedMonth && end.day > checkedDay)) && startYear--;
        startDate = (0, utils_1.createDate)(startYear, start.month, start.day);
        endDate = (0, utils_1.createDate)(startYear + 1, end.month, end.day);
    }
    else {
        // this is a range in the same year (e.g. Feb => June)
        startDate = (0, utils_1.createDate)(checkedYear, start.month, start.day);
        endDate = (0, utils_1.createDate)(checkedYear, end.month, end.day);
    }
    return startDate.getTime() <= checkedDate.getTime() && checkedDate.getTime() <= endDate.getTime();
}
exports.isInMonthDayRange = isInMonthDayRange;
//# sourceMappingURL=validaiton.js.map